if (!customElements.get('orrenlab-bundle-selector')) {
  class OrrenlabBundleSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.isSubmitting = false;
      this.requiresCartReview = false;

      this.offers = Array.from(this.querySelectorAll('.orrenlab-bundle__offer'));
      this.button = this.querySelector('.orrenlab-bundle__add');
      this.buttonLabel = this.button?.querySelector('[data-bundle-button-label]');
      this.error = this.querySelector('.orrenlab-bundle__error');
      this.liveRegion = this.querySelector('[data-bundle-live]');
      this.selectionStatus = this.querySelector('[data-bundle-selection-status]');
      this.sticky = this.querySelector('.orrenlab-bundle__sticky');
      this.stickyButton = this.querySelector('.orrenlab-bundle__sticky-action');
      this.stickySummary = this.querySelector('[data-bundle-sticky-summary]');
      this.sizeGuideButtons = Array.from(
        document.querySelectorAll('[data-size-guide-trigger], a[href="#orrenlab-size-guide"]'),
      );
      this.handleSizeGuideClick = (event) => {
        event.preventDefault();
        this.openSizeGuide(event.currentTarget);
      };

      // Dawn transforms the product information column. Moving the fixed
      // shortcut to document.body keeps it fixed to the viewport.
      if (this.sticky) document.body.appendChild(this.sticky);

      this.querySelectorAll('.orrenlab-bundle__main input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => this.selectTier(radio.value, true));
      });

      this.querySelectorAll('.orrenlab-bundle__size-input').forEach((radio) => {
        radio.addEventListener('change', () => {
          const offer = radio.closest('.orrenlab-bundle__offer');
          this.clearError();
          this.setSizeError(radio.closest('.orrenlab-bundle__size'), false);
          this.updateOfferPrice(offer);
          this.updateSelectionState(true);
        });
      });

      this.querySelectorAll('[data-bundle-quantity-control]').forEach((control) => {
        const offer = control.closest('.orrenlab-bundle__offer');
        control.querySelector('[data-bundle-quantity-decrease]')?.addEventListener('click', () => {
          this.setQuantity(offer, this.selectedQuantity(offer) - 1, true);
        });
        control.querySelector('[data-bundle-quantity-increase]')?.addEventListener('click', () => {
          this.setQuantity(offer, this.selectedQuantity(offer) + 1, true);
        });
      });

      this.button?.addEventListener('click', () => this.addBundle());
      this.sizeGuideButtons.forEach((button) => button.addEventListener('click', this.handleSizeGuideClick));
      this.stickyButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.reviewBundle();
      });

      // Show the shortcut only after the primary Add to cart control has
      // scrolled above the viewport, never while the customer approaches it.
      if (this.sticky && this.button && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          const hasPassedBuyButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.sticky.classList.toggle('is-visible', hasPassedBuyButton && !this.hasBlockingError());
        });
        this.visibilityObserver.observe(this.button);
      }

      const checkedTier = this.querySelector('.orrenlab-bundle__main input[type="radio"]:checked')?.value || '2';
      this.selectTier(checkedTier, false);
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.sizeGuideButtons?.forEach((button) => button.removeEventListener('click', this.handleSizeGuideClick));
      this.sticky?.remove();
      if (window.__orrenlabBundleSubmitting === this) window.__orrenlabBundleSubmitting = null;
    }

    selectTier(tier, announce) {
      this.offers.forEach((offer) => {
        const selected = offer.dataset.tier === String(tier);
        offer.classList.toggle('is-selected', selected);
        const radio = offer.querySelector('.orrenlab-bundle__main input[type="radio"]');
        if (radio) radio.checked = selected;
        offer.querySelectorAll('.orrenlab-bundle__size').forEach((fieldset) => this.setSizeError(fieldset, false));
      });

      this.clearError();
      this.updateSelectionState(false);

      if (announce) {
        const pairLabel = Number(tier) === 1 ? '1 pair' : `${tier} pairs`;
        this.announce(`${pairLabel} selected. Choose a size for each pair.`);
      }
    }

    selectedOffer() {
      return this.querySelector('.orrenlab-bundle__offer.is-selected');
    }

    selectedQuantity(offer = this.selectedOffer()) {
      return Number(offer?.dataset.quantity || offer?.dataset.tier || 0);
    }

    selectedSizeInputs(offer = this.selectedOffer()) {
      return Array.from(
        offer?.querySelectorAll('.orrenlab-bundle__size:not([hidden]) .orrenlab-bundle__size-input:checked') || [],
      );
    }

    discountForQuantity(quantity) {
      // Match the Shopify ORRENLAB-BUNDLE-N total discounts against $49.99 per pair.
      if (quantity === 1) return 999;
      if (quantity === 2) return 3998;
      return quantity >= 3 ? 2499 * quantity : 0;
    }

    setQuantity(offer, requestedQuantity, announce = false) {
      if (!offer || Number(offer.dataset.tier) !== 3) return;
      if (Number(requestedQuantity) < 3) {
        const twoPairOffer = this.offers.find((candidate) => candidate.dataset.tier === '2');
        if (!twoPairOffer) return;
        // Carry only the first two explicit choices, including gaps. Never invent a size.
        twoPairOffer.querySelectorAll('.orrenlab-bundle__size').forEach((fieldset) => {
          const selected = offer.querySelector(`[data-pair="${fieldset.dataset.pair}"] .orrenlab-bundle__size-input:checked`);
          fieldset.querySelectorAll('.orrenlab-bundle__size-input').forEach((input) => {
            input.checked = !input.disabled && input.value === selected?.value;
          });
        });
        this.selectTier('2', announce);
        twoPairOffer.querySelector('.orrenlab-bundle__main input[type="radio"]')?.focus({ preventScroll: true });
        return;
      }
      const quantity = Math.min(10, Math.max(3, Number(requestedQuantity) || 3));
      offer.dataset.quantity = String(quantity);
      offer.dataset.discountCents = String(this.discountForQuantity(quantity));

      offer.querySelectorAll('.orrenlab-bundle__size').forEach((fieldset) => {
        fieldset.hidden = Number(fieldset.dataset.pair) > quantity;
      });

      const output = offer.querySelector('[data-bundle-quantity-output]');
      const decrease = offer.querySelector('[data-bundle-quantity-decrease]');
      const increase = offer.querySelector('[data-bundle-quantity-increase]');
      if (output) output.textContent = `${quantity} pairs`;
      if (decrease) {
        decrease.disabled = false;
        decrease.setAttribute('aria-label', quantity === 3 ? 'Switch to 2 pairs' : 'Remove one pair');
      }
      if (increase) increase.disabled = quantity >= 10;

      this.clearError();
      this.updateSelectionState(announce);
    }

    updateOfferPrice(offer) {
      if (!offer) return;
      const tier = this.selectedQuantity(offer);
      const selectedInputs = this.selectedSizeInputs(offer);
      const unitPrice = Number(offer.dataset.unitPriceCents || 0);
      const selectedTotal = selectedInputs.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
      const compareTotal = selectedTotal + Math.max(0, tier - selectedInputs.length) * unitPrice;
      const discount = this.discountForQuantity(tier);
      const offerTotal = Math.max(0, compareTotal - discount);
      const perPair = tier ? offerTotal / tier : offerTotal;
      // Present bundle savings against the currently offered single-pair price.
      // Shopify's discount validation still uses the full base-price discount above.
      const individualTotal = compareTotal - tier * this.discountForQuantity(1);
      const bundleSaving = Math.max(0, individualTotal - offerTotal);

      const price = offer.querySelector('[data-bundle-price]');
      const comparePrice = offer.querySelector('[data-bundle-compare-price]');
      const saving = offer.querySelector('[data-bundle-saving]');
      const perPairLabel = offer.querySelector('[data-bundle-per-pair]');

      if (price) price.textContent = this.formatMoney(offerTotal);
      if (comparePrice) comparePrice.textContent = this.formatMoney(individualTotal);
      if (saving) saving.textContent = `Save ${this.formatMoney(bundleSaving)}`;
      if (perPairLabel) perPairLabel.textContent = `${this.formatMoney(perPair)} / pair`;
    }

    updateSelectionState(announce) {
      const offer = this.selectedOffer();
      const tier = this.selectedQuantity(offer);
      const selectedCount = this.selectedSizeInputs(offer).length;
      const missingCount = Math.max(0, tier - selectedCount);
      const isComplete = tier > 0 && missingCount === 0;

      this.updateOfferPrice(offer);
      this.updateStickySummary();

      if (this.selectionStatus) {
        if (isComplete) {
          this.selectionStatus.textContent = 'All sizes selected. Your bundle is ready to add.';
        } else if (missingCount === 1) {
          this.selectionStatus.textContent = 'Choose a size for 1 more pair to continue.';
        } else {
          this.selectionStatus.textContent = `Choose a size for ${missingCount} pairs to continue.`;
        }
      }

      // Incomplete selections remain actionable so the button can explain what is missing.
      if (this.button) this.button.disabled = this.isSubmitting;
      this.updateButtonLabel(isComplete);

      if (announce) {
        this.announce(isComplete ? 'All sizes selected. Your bundle is ready to add.' : this.selectionStatus?.textContent);
      }
    }

    updateButtonLabel(isComplete = false) {
      if (!this.buttonLabel) return;
      if (this.requiresCartReview) {
        this.buttonLabel.textContent = 'Edit bundle in cart';
      } else if (this.isSubmitting) {
        this.buttonLabel.textContent = 'Adding to cart…';
      } else {
        this.buttonLabel.textContent = 'Add to cart';
      }
    }

    updateStickySummary() {
      const offer = this.selectedOffer();
      const quantity = this.selectedQuantity(offer);
      const title = quantity === 1 ? '1 Pair' : `${quantity} Pairs`;
      const price = offer?.querySelector('[data-bundle-price]')?.textContent.trim();
      if (title && price && this.stickySummary) this.stickySummary.textContent = `${title} · ${price}`;
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (_error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    reviewBundle() {
      const offer = this.selectedOffer();
      offer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const firstMissing = offer?.querySelector('.orrenlab-bundle__size:not(:has(.orrenlab-bundle__size-input:checked)) .orrenlab-bundle__size-input:not([disabled])');
        (firstMissing || offer?.querySelector('.orrenlab-bundle__main input[type="radio"]'))?.focus({ preventScroll: true });
      }, 500);
    }

    openSizeGuide(trigger) {
      const details = document.querySelector('[data-orrenlab-size-guide]');
      if (!details) return;

      if (details.tagName === 'DETAILS') details.open = true;
      if (details.id) trigger?.setAttribute('aria-controls', details.id);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      details.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
      const focusTarget = details.querySelector('summary, [data-size-guide-heading]');
      window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), prefersReducedMotion ? 0 : 500);
    }

    setSizeError(fieldset, invalid) {
      if (!fieldset) return;
      fieldset.classList.toggle('is-invalid', invalid);
      const message = fieldset.querySelector('[data-bundle-size-error]');
      if (message) message.hidden = !invalid;
      fieldset.querySelectorAll('.orrenlab-bundle__size-input').forEach((input) => {
        if (invalid) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
    }

    async addBundle() {
      if (this.requiresCartReview) {
        window.location.assign(`${this.shopRoot()}cart`);
        return;
      }

      if (this.isSubmitting || (window.__orrenlabBundleSubmitting && window.__orrenlabBundleSubmitting !== this)) return;

      const offer = this.selectedOffer();
      const tier = this.selectedQuantity(offer);
      const selectedInputs = this.selectedSizeInputs(offer);

      if (!tier || selectedInputs.length !== tier) {
        this.showError('Please choose a size for each pair.', false);
        offer?.querySelectorAll?.('.orrenlab-bundle__size:not([hidden])').forEach((fieldset) => {
          this.setSizeError(fieldset, !fieldset.querySelector('.orrenlab-bundle__size-input:checked'));
        });
        const firstMissing = offer?.querySelector('.orrenlab-bundle__size:not([hidden]):not(:has(.orrenlab-bundle__size-input:checked)) .orrenlab-bundle__size-input:not([disabled])');
        firstMissing?.focus();
        return;
      }

      let transactionId = this.transactionId();
      const additionId = this.transactionId();
      let confirmedTier = tier;
      let expectedDiscount = Number(offer.dataset.discountCents || 0);
      let bundleCodes = this.bundleCodes(tier);
      let items = [];

      this.setLoading(true);
      this.clearError();
      let beforeCart;
      let previousCodes = [];
      let bundleAddAttempted = false;
      let bundleAdded = false;

      try {
        if (this.buttonLabel) this.buttonLabel.textContent = 'Checking your cart…';
        beforeCart = await this.getCart();
        previousCodes = this.sanitizedDiscountCodes(this.explicitDiscountCodes(beforeCart));

        const existingBundleLines = (beforeCart.items || []).filter(
          (item) => item.properties?.['_OrrenLab bundle'] && item.properties?.['_Bundle transaction'],
        );
        const existingTransactions = Array.from(
          new Set(existingBundleLines.map((item) => item.properties['_Bundle transaction'])),
        );
        if (existingTransactions.length > 1) {
          this.requiresCartReview = true;
          throw new Error('Your cart contains more than one bundle group. Review the cart before adding more pairs.');
        }
        if (existingTransactions.length === 1) {
          transactionId = existingTransactions[0];
          const existingQuantity = existingBundleLines.reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0,
          );
          confirmedTier = existingQuantity + tier;
          if (confirmedTier > 10) {
            throw new Error('Your bundle can contain up to 10 pairs. Edit the existing bundle in your cart to continue.');
          }
          expectedDiscount = this.discountForQuantity(confirmedTier);
          bundleCodes = this.bundleCodes(confirmedTier);
        }

        const pairOffset = confirmedTier - tier;
        items = selectedInputs.map((input, index) => ({
          id: Number(input.value),
          quantity: 1,
          properties: {
            '_OrrenLab bundle': `${confirmedTier}${confirmedTier === 1 ? ' Pair' : ' Pairs'}`,
            '_Bundle pair': String(pairOffset + index + 1),
            '_Bundle transaction': transactionId,
            '_Bundle addition': additionId,
            ...this.landingProperties(),
          },
        }));

        bundleAddAttempted = true;
        if (this.buttonLabel) this.buttonLabel.textContent = 'Adding to cart…';
        await this.requestJSON('cart/add.js', { items });
        bundleAdded = true;

        const preservedCodes = previousCodes.filter((code) => !code.toUpperCase().startsWith('ORRENLAB-BUNDLE-'));
        const desiredCodes = [...preservedCodes, ...bundleCodes];

        if (this.buttonLabel) this.buttonLabel.textContent = 'Applying bundle savings…';
        const confirmedCart = await this.requestJSON('cart/update.js', { discount: desiredCodes.join(',') });
        if (!this.transactionIsComplete(confirmedCart, beforeCart, transactionId, confirmedTier, expectedDiscount, preservedCodes)) {
          throw new Error('The bundle discount could not be confirmed.');
        }

        if (this.buttonLabel) this.buttonLabel.textContent = 'Opening your cart…';
        document.dispatchEvent(
          new CustomEvent('orrenlab:bundle-added', {
            detail: {
              productId: Number(this.dataset.productId),
              tier: confirmedTier,
              variantIds: items.map((item) => item.id),
              transactionId,
            },
          }),
        );
        window.location.assign(`${this.shopRoot()}cart`);
      } catch (error) {
        if (!beforeCart) {
          this.requiresCartReview = true;
          this.showError(error.message || 'Your cart may contain this bundle. Review your cart before retrying.', true);
          this.setLoading(false);
          return;
        }

        if (!bundleAddAttempted) {
          this.showError(error.message || 'The bundle could not be added. Nothing was added to your cart.', true);
          this.setLoading(false);
          return;
        }

        // A lost response does not mean Shopify rejected the add. Reconcile
        // this addition before permitting another attempt.
        const uncertainAdd = !bundleAdded && !error.cartRequestRejected;
        // A timed-out write can still finish server-side after reconciliation.
        // Keep review required even if the first cleanup appears successful.
        if (error.cartRequestTimedOut) this.requiresCartReview = true;
        if (this.buttonLabel) this.buttonLabel.textContent = 'Checking your cart…';
        const recovery = await this.recoverAddition(additionId, previousCodes);

        if (recovery === 'rolled-back' && !this.requiresCartReview) {
          this.showError('We could not confirm the bundle discount, so nothing was added. Please try again.', true);
        } else if (recovery === 'needs-review' || this.requiresCartReview || (recovery === 'not-added' && uncertainAdd)) {
          // An empty read can race an in-flight add. Do not allow blind retry.
          this.requiresCartReview = true;
          this.showError('We could not confirm your bundle. Review your cart before retrying.', true);
        } else {
          this.showError(error.message || 'The bundle could not be added. Nothing was added to your cart.', true);
        }
        this.setLoading(false);
      }
    }

    shopRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    landingProperties() {
      const landingPageId = this.dataset.landingPageId;
      if (!landingPageId) return {};

      const params = new URLSearchParams(window.location.search);
      const properties = {
        '_Landing page': landingPageId,
        '_Landing path': this.dataset.landingPageHandle
          ? `/pages/${this.dataset.landingPageHandle}`
          : window.location.pathname,
      };
      const campaign = params.get('utm_campaign');
      const content = params.get('utm_content');
      const source = params.get('utm_source');
      const medium = params.get('utm_medium');

      if (source) properties['_UTM source'] = source.slice(0, 255);
      if (medium) properties['_UTM medium'] = medium.slice(0, 255);
      if (campaign) properties['_UTM campaign'] = campaign.slice(0, 255);
      if (content) properties['_UTM content'] = content.slice(0, 255);
      return properties;
    }

    async requestJSON(path, body) {
      return this.fetchCartJSON(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
    }

    async getCart() {
      return this.fetchCartJSON('cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    }

    async fetchCartJSON(path, options) {
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          const error = new Error('Your connection is taking longer than expected. Please review your cart before trying again.');
          error.cartRequestTimedOut = true;
          reject(error);
          controller.abort();
        }, 15000);
      });
      try {
        // Bound both headers and body parsing, including a stalled JSON body.
        return await Promise.race([
          (async () => {
            const response = await fetch(`${this.shopRoot()}${path}`, { ...options, signal: controller.signal });
            const result = await response.json().catch(() => {
              if (response.ok) throw new Error('The cart response could not be confirmed. Please review your cart.');
              return {};
            });
            if (!response.ok) {
              const error = new Error(result.description || result.message || 'The cart could not be updated.');
              error.cartRequestRejected = response.status >= 400 && response.status < 500 && response.status !== 408;
              throw error;
            }
            return result;
          })(),
          timeout,
        ]);
      } finally {
        clearTimeout(timer);
      }
    }

    transactionLines(cart, transactionId) {
      return (cart?.items || []).filter((item) => item.properties?.['_Bundle transaction'] === transactionId);
    }

    transactionIsComplete(cart, beforeCart, transactionId, tier, expectedDiscount, preservedCodes) {
      const lines = this.transactionLines(cart, transactionId);
      const quantity = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      const beforeDiscount = Number(beforeCart?.total_discount || 0);
      const afterDiscount = Number(cart?.total_discount || 0);
      const discountDelta = Math.max(0, afterDiscount - beforeDiscount);
      const bundleLineDiscount = lines.reduce(
        (sum, line) => sum + Math.max(0, Number(line.original_line_price || 0) - Number(line.final_line_price || 0)),
        0,
      );
      const appliedCodes = this.explicitDiscountCodes(cart).map((code) => code.toUpperCase());
      const previousCodesPreserved = preservedCodes.every((code) => appliedCodes.includes(code.toUpperCase()));
      return (
        quantity === tier &&
        previousCodesPreserved &&
        appliedCodes.includes(`ORRENLAB-BUNDLE-${tier}`) &&
        Math.max(discountDelta, bundleLineDiscount) >= Math.max(0, expectedDiscount - 1)
      );
    }

    explicitDiscountCodes(cart) {
      const codes = new Set();
      (cart?.cart_level_discount_applications || []).forEach((application) => {
        if (application.type === 'discount_code' && application.title) codes.add(application.title);
      });
      (cart?.items || []).forEach((item) => {
        (item.discount_allocations || item.line_level_discount_allocations || []).forEach((allocation) => {
          const application = allocation.discount_application || allocation;
          if (application.type === 'discount_code' && application.title) codes.add(application.title);
        });
      });
      return Array.from(codes);
    }

    bundleCodes(quantity) {
      return [`ORRENLAB-BUNDLE-${quantity}`];
    }

    sanitizedDiscountCodes(codes) {
      return codes.filter((code) => String(code).toUpperCase() !== 'ORRENLAB-BUNDLE-SHIP');
    }

    async recoverAddition(additionId, previousCodes) {
      previousCodes = this.sanitizedDiscountCodes(previousCodes);
      let cart;
      try {
        cart = await this.getCart();
      } catch (_error) {
        return 'needs-review';
      }

      const lines = (cart?.items || []).filter(
        (item) => item.properties?.['_Bundle addition'] === additionId,
      );
      if (!lines.length) return 'not-added';

      const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
      try {
        await this.requestJSON('cart/update.js', {
          updates,
          discount: previousCodes.join(','),
        });
        const recoveredCart = await this.getCart();
        const additionRemains = (recoveredCart?.items || []).some(
          (item) => item.properties?.['_Bundle addition'] === additionId,
        );
        return additionRemains ? 'needs-review' : 'rolled-back';
      } catch (_error) {
        return 'needs-review';
      }
    }

    transactionId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      window.__orrenlabBundleSubmitting = isLoading ? this : null;
      this.updateSelectionState(false);
      if (this.button) this.button.setAttribute('aria-busy', String(isLoading));
    }

    hasBlockingError() {
      return Boolean(this.error && !this.error.hidden);
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.sticky?.classList.remove('is-visible');
    }

    showError(message, focusError) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.sticky?.classList.remove('is-visible');
      this.updateButtonLabel(this.selectedSizeInputs().length === this.selectedQuantity());
      this.error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (focusError) window.setTimeout(() => this.error.focus({ preventScroll: true }), 250);
    }

    announce(message) {
      if (!this.liveRegion || !message) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.liveRegion.textContent = message;
      }, 30);
    }
  }

  customElements.define('orrenlab-bundle-selector', OrrenlabBundleSelector);
}
